import { useCallback, useState } from "react";
import { Button, Input, Modal, Select, message as antMessage } from "antd";
import { ApolloError } from "@apollo/client";
import { ArrowLeft, Check, ChevronRight, CircleHelp, MessageSquare } from "lucide-react";

import { useSubmitSupportRequestMutation } from "graphql/mutations/submit-support-request.generated";
import { SUPPORT_ISSUE_TYPES, SupportRequestKind } from "constants/constants";

type HelpFeedbackStep = "options" | "feedback" | "support" | "feedback-sent" | "support-sent";

interface HelpFeedbackModalProps {
	open: boolean;
	onClose: () => void;
}

const DEFAULT_ISSUE_TYPE = SUPPORT_ISSUE_TYPES[0];

const HelpFeedbackModal = ({ open, onClose }: HelpFeedbackModalProps) => {
	const [step, setStep] = useState<HelpFeedbackStep>("options");
	const [issueType, setIssueType] = useState<string>(DEFAULT_ISSUE_TYPE);
	const [messageText, setMessageText] = useState("");

	const [submitSupportRequestMutation, { loading: isSending }] = useSubmitSupportRequestMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only"
	});

	const isMessageEmpty = messageText.trim().length === 0;

	const goToOptions = useCallback(() => {
		setStep("options");
		setMessageText("");
	}, []);

	// The next time the modal opens, it starts from the options again.
	const resetForm = useCallback(() => {
		setStep("options");
		setIssueType(DEFAULT_ISSUE_TYPE);
		setMessageText("");
	}, []);

	const sendRequest = useCallback(
		async (kind: string, stepAfterSending: HelpFeedbackStep) => {
			try {
				await submitSupportRequestMutation({
					variables: {
						kind,
						issue_type: kind === SupportRequestKind.Support ? issueType : null,
						message: messageText.trim()
					}
				});
				setStep(stepAfterSending);
			} catch (error) {
				const serverMessage = (error as ApolloError).graphQLErrors?.[0]?.message;
				antMessage.error(serverMessage || "Your request could not be sent. Please try again.");
			}
		},
		[submitSupportRequestMutation, issueType, messageText]
	);

	const renderOptions = () => (
		<div className="help-feedback-options">
			<button type="button" className="help-feedback-option" onClick={() => setStep("feedback")}>
				<MessageSquare size={16} className="help-feedback-option-icon" />
				<span className="help-feedback-option-text">
					<span className="help-feedback-option-title">Share Product Feedback</span>
					<span className="help-feedback-option-description">Tell us what you love or what could be improved.</span>
				</span>
				<ChevronRight size={16} className="help-feedback-option-arrow" />
			</button>
			<button type="button" className="help-feedback-option" onClick={() => setStep("support")}>
				<CircleHelp size={16} className="help-feedback-option-icon" />
				<span className="help-feedback-option-text">
					<span className="help-feedback-option-title">Get Support</span>
					<span className="help-feedback-option-description">Need help with an issue? Contact our support team.</span>
				</span>
				<ChevronRight size={16} className="help-feedback-option-arrow" />
			</button>
		</div>
	);

	const renderMessageInput = (placeholder: string) => (
		<Input.TextArea
			className="help-feedback-textarea"
			value={messageText}
			onChange={event => setMessageText(event.target.value)}
			placeholder={placeholder}
			autoSize={{ minRows: 3, maxRows: 8 }}
		/>
	);

	const renderFeedbackForm = () => (
		<div className="help-feedback-form">
			{renderMessageInput("What's on your mind?")}
			<Button
				type="primary"
				className="help-feedback-submit"
				loading={isSending}
				disabled={isMessageEmpty}
				onClick={() => sendRequest(SupportRequestKind.Feedback, "feedback-sent")}
			>
				Submit Feedback
			</Button>
		</div>
	);

	const renderSupportForm = () => (
		<div className="help-feedback-form">
			<label className="help-feedback-label">Issue Type</label>
			<Select
				className="help-feedback-select"
				value={issueType}
				onChange={setIssueType}
				options={SUPPORT_ISSUE_TYPES.map(type => ({ value: type, label: type }))}
			/>
			<label className="help-feedback-label">Description</label>
			{renderMessageInput("How can we help?")}
			<Button
				type="primary"
				className="help-feedback-submit"
				loading={isSending}
				disabled={isMessageEmpty}
				onClick={() => sendRequest(SupportRequestKind.Support, "support-sent")}
			>
				Send
			</Button>
		</div>
	);

	const renderConfirmation = (title: string, text: string) => (
		<div className="help-feedback-confirmation">
			<span className="help-feedback-confirmation-icon">
				<Check size={18} />
			</span>
			<span className="help-feedback-confirmation-title">{title}</span>
			<span className="help-feedback-confirmation-text">{text}</span>
			<Button className="help-feedback-close" onClick={onClose}>
				Close
			</Button>
		</div>
	);

	const titleByStep: Record<HelpFeedbackStep, string> = {
		options: "Help & Feedback",
		feedback: "Share Feedback",
		support: "Get Support",
		"feedback-sent": "",
		"support-sent": ""
	};
	const isFormStep = step === "feedback" || step === "support";
	const isConfirmationStep = step === "feedback-sent" || step === "support-sent";

	return (
		<Modal
			open={open}
			onCancel={onClose}
			afterClose={resetForm}
			footer={null}
			centered
			width={400}
			className="help-feedback-modal"
			title={
				!isConfirmationStep && (
					<div className="help-feedback-title">
						{isFormStep && (
							<button type="button" className="help-feedback-back" onClick={goToOptions} aria-label="Back">
								<ArrowLeft size={16} />
							</button>
						)}
						<span>{titleByStep[step]}</span>
					</div>
				)
			}
		>
			{step === "options" && renderOptions()}
			{step === "feedback" && renderFeedbackForm()}
			{step === "support" && renderSupportForm()}
			{step === "feedback-sent" && renderConfirmation("Feedback Sent", "Thanks for sharing! We're always working to improve!")}
			{step === "support-sent" && renderConfirmation("Message Sent", "Thanks for reaching out! Our team will get back to you soon.")}
		</Modal>
	);
};

export default HelpFeedbackModal;
